/**
 * Shared split-flap audio: swoosh and click sounds.
 * Used by SplitFlapDisplayPage and TitleLogosPage.
 */

export const SWOOSH_GAIN_PCT_DEFAULT = 100;
export const SWOOSH_FREQ_MIN = 400;
export const SWOOSH_FREQ_MAX = 4000;
export const SWOOSH_FREQ_DEFAULT = 3850;
export const SPLITFLAP_SOUND_SETTINGS_STORAGE_KEY = "splitflap-display-sound-settings";
export const SPLITFLAP_SOUND_DEFAULTS = {
  soundOn: true,
  swooshOn: true,
  swooshGainPct: SWOOSH_GAIN_PCT_DEFAULT,
  swooshFreqHz: SWOOSH_FREQ_DEFAULT,
  soundVariant: 6,
  modalTapFreq1Hz: 600,
  modalTapFreq2Hz: 1200,
  modalTapGain1Pct: 7,
  modalTapGain2Pct: 12,
};

let sharedAudioContext = null;
const WebAudioCtx =
  typeof window !== "undefined" &&
  (window.AudioContext || window.webkitAudioContext);

/**
 * Returns the shared AudioContext only if it already exists.
 * The context is created lazily in resumeClickAudio() (on first user gesture)
 * so we avoid "AudioContext was not allowed to start" in browsers that require
 * a user gesture before creating/resuming context.
 */
export function getAudioContext() {
  return sharedAudioContext;
}

/**
 * Ensure we have an AudioContext instance.
 * May still be suspended until a user gesture; callers should handle that.
 */
export function ensureAudioContext() {
  try {
    if (!sharedAudioContext && WebAudioCtx) {
      sharedAudioContext = new WebAudioCtx();
    }
  } catch (e) {
    console.warn("Could not create split-flap AudioContext", e);
  }
  return sharedAudioContext;
}

function clampNumber(value, min, max, fallback) {
  return typeof value === "number" ? Math.max(min, Math.min(max, value)) : fallback;
}

/**
 * Resolve split-flap sound settings from storage with safe defaults.
 * This is the shared source for playback settings outside the settings page.
 */
export function getSplitFlapSoundSettings() {
  if (typeof window === "undefined" || !window.localStorage) {
    return { ...SPLITFLAP_SOUND_DEFAULTS };
  }

  try {
    const raw = window.localStorage.getItem(SPLITFLAP_SOUND_SETTINGS_STORAGE_KEY);
    if (!raw) return { ...SPLITFLAP_SOUND_DEFAULTS };

    const data = JSON.parse(raw);
    if (!data || typeof data !== "object") return { ...SPLITFLAP_SOUND_DEFAULTS };

    return {
      soundOn:
        typeof data.soundOn === "boolean"
          ? data.soundOn
          : SPLITFLAP_SOUND_DEFAULTS.soundOn,
      swooshOn:
        typeof data.swooshOn === "boolean"
          ? data.swooshOn
          : SPLITFLAP_SOUND_DEFAULTS.swooshOn,
      swooshGainPct: clampNumber(
        data.swooshGainPct,
        0,
        100,
        SPLITFLAP_SOUND_DEFAULTS.swooshGainPct
      ),
      swooshFreqHz: clampNumber(
        data.swooshFreqHz,
        SWOOSH_FREQ_MIN,
        SWOOSH_FREQ_MAX,
        SPLITFLAP_SOUND_DEFAULTS.swooshFreqHz
      ),
      soundVariant:
        typeof data.soundVariant === "number" &&
        data.soundVariant >= 1 &&
        data.soundVariant <= 7
          ? data.soundVariant
          : SPLITFLAP_SOUND_DEFAULTS.soundVariant,
      modalTapFreq1Hz: clampNumber(
        data.modalTapFreq1Hz,
        100,
        3000,
        SPLITFLAP_SOUND_DEFAULTS.modalTapFreq1Hz
      ),
      modalTapFreq2Hz: clampNumber(
        data.modalTapFreq2Hz,
        100,
        3000,
        SPLITFLAP_SOUND_DEFAULTS.modalTapFreq2Hz
      ),
      modalTapGain1Pct: clampNumber(
        data.modalTapGain1Pct,
        0,
        100,
        SPLITFLAP_SOUND_DEFAULTS.modalTapGain1Pct
      ),
      modalTapGain2Pct: clampNumber(
        data.modalTapGain2Pct,
        0,
        100,
        SPLITFLAP_SOUND_DEFAULTS.modalTapGain2Pct
      ),
    };
  } catch (e) {
    console.warn("Could not load split-flap sound settings", e);
    return { ...SPLITFLAP_SOUND_DEFAULTS };
  }
}

/**
 * Resumes the shared AudioContext if it already exists. Does NOT create the context
 * (creation happens only on first user gesture in the listener below) so we avoid
 * "AudioContext was not allowed to start" when resumeClickAudio() is called from
 * an effect before any user interaction.
 */
export function resumeClickAudio() {
  const ctx = ensureAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
}

// Create AudioContext only on first user interaction so we avoid "not allowed to start" warning.
if (typeof document !== "undefined" && document.addEventListener) {
  const once = () => {
    ensureAudioContext();
    resumeClickAudio();
    document.removeEventListener("click", once);
    document.removeEventListener("touchstart", once);
    document.removeEventListener("keydown", once);
  };
  document.addEventListener("click", once, { once: true, passive: true });
  document.addEventListener("touchstart", once, { once: true, passive: true });
  document.addEventListener("keydown", once, { once: true });
}

/**
 * Play a short background swoosh (filtered noise sweep).
 * @param {{ gainPct?: number, startFreqHz?: number }} options
 */
export function playSwoosh(options = {}) {
  try {
    const ctx = ensureAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      ctx.resume().then(() => playSwoosh(options)).catch(() => {});
      return;
    }
    const gainPct = Math.max(0, Math.min(100, options.gainPct ?? SWOOSH_GAIN_PCT_DEFAULT)) / 100;
    const startFreq = Math.max(SWOOSH_FREQ_MIN, Math.min(SWOOSH_FREQ_MAX, options.startFreqHz ?? SWOOSH_FREQ_DEFAULT));
    const t0 = ctx.currentTime;
    const duration = 0.28;
    const gainNode = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(startFreq, t0);
    filter.frequency.linearRampToValueAtTime(120, t0 + duration);
    filter.Q.value = 0.7;
    const bufferSize = Math.ceil(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(ctx.destination);
    const peakGain = 0.22 * gainPct;
    gainNode.gain.setValueAtTime(peakGain, t0);
    gainNode.gain.exponentialRampToValueAtTime(Math.max(0.001, 0.001 * gainPct), t0 + duration);
    source.start(t0);
    source.stop(t0 + duration);
  } catch (e) {
    console.warn("Swoosh sound failed", e);
  }
}

/**
 * Play click variant (1–7). Variant 6 = modal tap with variant6Options.
 * @param {number} variant
 * @param {{ freqs: [number, number], gain1Pct?: number, gain2Pct?: number } | null} variant6Options
 */
export function playClickVariant(variant, variant6Options) {
  try {
    const ctx = ensureAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      ctx
        .resume()
        .then(() => playClickVariant(variant, variant6Options))
        .catch(() => {});
      return;
    }
    const t0 = ctx.currentTime;
    const peakGain = 0.65;
    const tailGain = 0.01;
    const decayMs = 0.018;

    if (variant >= 1 && variant <= 3) {
      const gain = ctx.createGain();
      const osc = ctx.createOscillator();
      osc.connect(gain);
      gain.connect(ctx.destination);
      const [freq, duration] =
        variant === 1 ? [800, 0.014] : variant === 2 ? [1200, 0.012] : [600, 0.016];
      osc.frequency.value = freq;
      osc.type = "square";
      gain.gain.setValueAtTime(peakGain, t0);
      gain.gain.exponentialRampToValueAtTime(tailGain, t0 + duration);
      osc.start(t0);
      osc.stop(t0 + Math.min(duration + 0.003, 0.022));
      return;
    }

    if (variant === 4) {
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 280;
      filter.Q.value = 8;
      const osc = ctx.createOscillator();
      osc.type = "square";
      osc.frequency.value = 80;
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(peakGain, t0);
      gain.gain.exponentialRampToValueAtTime(tailGain, t0 + decayMs);
      osc.start(t0);
      osc.stop(t0 + 0.022);
      return;
    }

    if (variant === 5) {
      const gain = ctx.createGain();
      const bufferSize = ctx.sampleRate * 0.014;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.7;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(1, t0);
      gain.gain.exponentialRampToValueAtTime(tailGain, t0 + 0.012);
      source.start(t0);
      source.stop(t0 + 0.014);
      return;
    }

    if (variant === 6) {
      const freqs = variant6Options?.freqs && variant6Options.freqs.length === 2 ? variant6Options.freqs : [420, 1180];
      const [f1, f2] = freqs;
      const g1 = Math.max(0, Math.min(100, variant6Options?.gain1Pct ?? 100)) / 100;
      const g2 = Math.max(0, Math.min(100, variant6Options?.gain2Pct ?? 100)) / 100;
      const gain1 = ctx.createGain();
      const gain2 = ctx.createGain();
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      osc1.type = "sine";
      osc2.type = "sine";
      osc1.frequency.value = f1;
      osc2.frequency.value = f2;
      osc1.connect(gain1);
      osc2.connect(gain2);
      gain1.connect(ctx.destination);
      gain2.connect(ctx.destination);
      gain1.gain.setValueAtTime(peakGain * g1, t0);
      gain2.gain.setValueAtTime(peakGain * g2, t0);
      gain1.gain.exponentialRampToValueAtTime(Math.max(0.001, tailGain * g1), t0 + 0.016);
      gain2.gain.exponentialRampToValueAtTime(Math.max(0.001, tailGain * g2), t0 + 0.016);
      osc1.start(t0);
      osc2.start(t0);
      osc1.stop(t0 + 0.018);
      osc2.stop(t0 + 0.018);
      return;
    }

    if (variant === 7) {
      const gain = ctx.createGain();
      const osc = ctx.createOscillator();
      osc.type = "square";
      osc.frequency.value = 180;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(peakGain, t0);
      gain.gain.exponentialRampToValueAtTime(tailGain, t0 + 0.012);
      osc.start(t0);
      osc.stop(t0 + 0.014);
    }
  } catch (e) {
    console.warn("Split-flap click sound failed", e);
  }
}
